import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const statusConfig = {
  active:    { label: 'Activo',     bg: '#D1FAE5', color: '#065F46' },
  inactive:  { label: 'Inactivo',   bg: '#FEF3C7', color: '#92400E' },
  completed: { label: 'Completado', bg: '#F3F4F6', color: '#374151' },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || { label: status, bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, backgroundColor: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
};

const IconBtn = ({ onClick, to, title, variant = 'default', children }) => {
  const variants = {
    default: { bg: '#F4F6FB', color: '#4B5563', hover: '#E5E7EB' },
    primary: { bg: '#EEF0FB', color: '#4F63D2', hover: '#dde1f7' },
    danger:  { bg: '#FEE2E2', color: '#EF4444', hover: '#fecaca' },
    success: { bg: '#D1FAE5', color: '#10B981', hover: '#a7f3d0' },
  };
  const v = variants[variant];
  const style = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, border: 'none', cursor: 'pointer', backgroundColor: v.bg, color: v.color, textDecoration: 'none', transition: 'background 0.15s', flexShrink: 0 };
  const handlers = {
    onMouseEnter: e => e.currentTarget.style.backgroundColor = v.hover,
    onMouseLeave: e => e.currentTarget.style.backgroundColor = v.bg,
  };
  if (to) return <Link to={to} title={title} style={style} {...handlers}>{children}</Link>;
  return <button onClick={onClick} title={title} style={style} {...handlers}>{children}</button>;
};

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [deactivateModal, setDeactivateModal] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  const fetchClients = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (search) params.search = search;
      if (status) params.status = status;
      if (includeInactive) params.include_inactive = 1;
      const res = await api.get('/clients', { params });
      setClients(res.data.data);
      setPagination(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { setCurrentPage(1); fetchClients(1); }, [search, status, includeInactive]);

  const handleDeactivate = async () => {
    if (!deactivateModal) return;
    setDeactivating(true);
    try {
      const res = await api.delete(`/clients/${deactivateModal.id}`, { data: { deactivation_reason: deactivateReason } });
      toast.success(`Cliente desactivado.${res.data.affected_case_files > 0 ? ` ${res.data.affected_case_files} expediente(s) quedan en historial.` : ''}`);
      setDeactivateModal(null);
      setDeactivateReason('');
      fetchClients(currentPage);
    } catch { toast.error('Error al desactivar cliente'); }
    finally { setDeactivating(false); }
  };

  const handleReactivate = async (client) => {
    try {
      await api.post(`/clients/${client.id}/reactivate`);
      toast.success('Cliente reactivado');
      fetchClients(currentPage);
    } catch { toast.error('Error al reactivar'); }
  };

  return (
    <div>
      <PageHeader
        title={<>Clientes {pagination.total > 0 && <span style={{ fontSize: 13, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2', marginLeft: 8 }}>{pagination.total}</span>}</>}
        subtitle="Gestiona los clientes de la notaría"
        actions={
          <Link to="/clients/create" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, textDecoration: 'none', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#4458c4'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}>
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Cliente
          </Link>
        }
      />

      {/* Filtros */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Búsqueda */}
          <div style={{ position: 'relative', flex: '1', minWidth: 200 }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: '#9CA3AF', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por nombre, email, teléfono..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoComplete="off"
              style={{ width: '100%', padding: '8px 32px 8px 32px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#F9FAFB', outline: 'none', transition: 'border-color 0.15s' }}
              onFocus={e => { e.target.style.borderColor = '#4F63D2'; e.target.style.backgroundColor = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.backgroundColor = '#F9FAFB'; }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2, display: 'flex' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>

          {/* Estado */}
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            style={{ padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#374151', backgroundColor: '#F9FAFB', outline: 'none', cursor: 'pointer', minWidth: 160 }}
          >
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
            <option value="completed">Completado</option>
          </select>

          {/* Desactivados */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4B5563', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={e => setIncludeInactive(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: '#4F63D2', cursor: 'pointer' }}
            />
            Ver desactivados
          </label>
        </div>

        {search && pagination.total !== undefined && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF' }}>
            {pagination.total > 0 ? `${pagination.total} resultado(s) para` : 'Sin resultados para'} <strong style={{ color: '#4B5563' }}>"{search}"</strong>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', flexDirection: 'column', gap: 12 }}>
            <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#9CA3AF' }}>Cargando clientes...</span>
          </div>
        ) : clients.length === 0 ? (
          <EmptyState
            icon="👥"
            title="No hay clientes registrados"
            subtitle="Agrega tu primer cliente para comenzar"
            action={<Link to="/clients/create" style={{ padding: '7px 16px', borderRadius: 8, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>+ Nuevo Cliente</Link>}
          />
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  {['Nombre', 'Contacto', 'Teléfono', 'Email', 'Responsable', 'Estado', ''].map((h, i) => (
                    <th key={i} className={[1, 2, 3, 4].includes(i) ? 'mobile-hide' : ''}
                      style={{ padding: '10px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}
                    style={{ opacity: !client.active ? 0.6 : 1, backgroundColor: '#fff', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                    {/* Nombre */}
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: client.active ? '#EEF0FB' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: client.active ? '#4F63D2' : '#9CA3AF', fontSize: 12, flexShrink: 0 }}>
                          {client.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <Link to={`/clients/${client.id}`} style={{ fontSize: 13, fontWeight: 600, color: '#111827', textDecoration: 'none' }}>
                            {client.name}
                          </Link>
                          {!client.active && (
                            <span style={{ display: 'inline-block', marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Desactivado</span>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* Contacto */}
                    <td className="mobile-hide" style={{ padding: '11px 14px', fontSize: 13, color: '#4B5563' }}>
                      {client.contact_name ? (
                        <div>
                          <span style={{ fontWeight: 500 }}>{client.contact_name}</span>
                          {client.contact_role && <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 5 }}>({client.contact_role})</span>}
                        </div>
                      ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    {/* Teléfono */}
                    <td className="mobile-hide" style={{ padding: '11px 14px', fontSize: 13, color: '#4B5563' }}>
                      {client.phone || <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    {/* Email */}
                    <td className="mobile-hide" style={{ padding: '11px 14px', fontSize: 13, color: '#4B5563', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {client.email || <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    {/* Responsable */}
                    <td className="mobile-hide" style={{ padding: '11px 14px', fontSize: 13, color: '#4B5563' }}>
                      {client.assigned_user?.name || <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>
                    {/* Estado */}
                    <td style={{ padding: '11px 14px' }}>
                      <StatusBadge status={client.status} />
                    </td>
                    {/* Acciones */}
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <IconBtn to={`/clients/${client.id}`} title="Ver detalle" variant="primary">
                          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </IconBtn>
                        {client.active ? (
                          <>
                            <IconBtn to={`/clients/${client.id}/edit`} title="Editar">
                              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </IconBtn>
                            <IconBtn onClick={() => { setDeactivateModal(client); setDeactivateReason(''); }} title="Desactivar" variant="danger">
                              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                            </IconBtn>
                          </>
                        ) : (
                          <IconBtn onClick={() => handleReactivate(client)} title="Reactivar" variant="success">
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          </IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {pagination.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #F3F4F6', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                  Mostrando {pagination.from}–{pagination.to} de {pagination.total} clientes
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[
                    { label: '‹', page: currentPage - 1, disabled: currentPage === 1 },
                    ...Array.from({ length: pagination.last_page }, (_, i) => ({ label: i + 1, page: i + 1, active: currentPage === i + 1 })),
                    { label: '›', page: currentPage + 1, disabled: currentPage === pagination.last_page },
                  ].map((item, i) => (
                    <button key={i} disabled={item.disabled}
                      onClick={() => { if (!item.disabled) { setCurrentPage(item.page); fetchClients(item.page); } }}
                      style={{ minWidth: 30, height: 30, borderRadius: 7, border: '1.5px solid', borderColor: item.active ? '#4F63D2' : '#E5E7EB', backgroundColor: item.active ? '#4F63D2' : '#fff', color: item.active ? '#fff' : item.disabled ? '#D1D5DB' : '#374151', fontWeight: item.active ? 700 : 400, fontSize: 13, cursor: item.disabled ? 'not-allowed' : 'pointer', padding: '0 6px' }}>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal desactivación */}
      {deactivateModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '28px', maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <svg style={{ width: 24, height: 24, color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h5 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827' }}>Desactivar cliente</h5>
              <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
                Estás a punto de desactivar a <strong>{deactivateModal.name}</strong>.
              </p>
            </div>

            <div style={{ backgroundColor: '#FEF3C7', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#92400E' }}>
              ⚠️ El cliente y su información se conservarán como historial, pero no aparecerá en listas activas.
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Motivo de desactivación <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(opcional)</span></label>
              <textarea
                rows={3}
                placeholder="Ej. Trámite finalizado, cliente solicitó cancelar..."
                value={deactivateReason}
                onChange={e => setDeactivateReason(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
                onFocus={e => e.target.style.borderColor = '#4F63D2'}
                onBlur={e => e.target.style.borderColor = '#E5E7EB'}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeactivateModal(null); setDeactivateReason(''); }} disabled={deactivating}
                style={{ padding: '8px 20px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Cancelar
              </button>
              <button onClick={handleDeactivate} disabled={deactivating}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: deactivating ? 0.7 : 1 }}>
                {deactivating ? 'Desactivando...' : 'Sí, desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;
